// Decoding an uploaded CSV file's bytes into text.
//
// The employee importer accepts a file the admin produced with "Excel → Save As
// CSV". On a Traditional-Chinese Windows, Excel's default "CSV (Comma delimited)"
// writes the file in the system ANSI codepage (Big5 / CP950), NOT UTF-8. If we
// blindly decode those bytes as UTF-8, every Chinese name is mojibake'd BEFORE it
// ever reaches the backend, is then encrypted verbatim, and round-trips back out
// garbled on export. So we must not assume UTF-8: try UTF-8 strictly, and fall
// back to Big5 when the bytes aren't valid UTF-8.
//
// TextDecoder ships 'big5' in every evergreen browser (and in Node), so this
// needs no dependency.

/** Strip a leading UTF-8 BOM (U+FEFF) if present — Excel prepends one for UTF-8
 *  CSVs, and it would otherwise ride into the first header cell. */
function stripBom(s: string): string {
  return s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}

/**
 * Decode raw CSV file bytes to text, detecting UTF-8 vs Big5/CP950.
 *
 * Strategy: attempt a STRICT UTF-8 decode (fatal: true). Valid UTF-8 — including
 * ASCII-only and a BOM'd UTF-8 file — decodes cleanly. If it throws, the bytes
 * are not valid UTF-8, which for our inputs means a legacy Big5 Excel export, so
 * we decode as Big5. A leading BOM is stripped in either case.
 */
export function decodeCsvBytes(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  try {
    const utf8 = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return stripBom(utf8);
  } catch {
    // Not valid UTF-8 → assume Big5 (CP950), the zh-TW Excel Save-As default.
    // Big5 is non-fatal by nature; stripBom handles the (unlikely) BOM case.
    const big5 = new TextDecoder("big5").decode(bytes);
    return stripBom(big5);
  }
}

/** Read a File and decode it with {@link decodeCsvBytes}. Kept separate from the
 *  pure decoder so the byte→text logic is unit-testable without a File/FileReader. */
export function readCsvFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (!(result instanceof ArrayBuffer)) {
        reject(new Error("expected ArrayBuffer"));
        return;
      }
      resolve(decodeCsvBytes(result));
    };
    reader.onerror = () => reject(reader.error ?? new Error("file read failed"));
    reader.readAsArrayBuffer(file);
  });
}
